import * as amplitude from '@amplitude/analytics-browser'
import type { CEFRLevel } from '@/types/database'
import { captureException } from '@/lib/sentry'

const AMPLITUDE_API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY

export function initAnalytics() {
  if (!AMPLITUDE_API_KEY) {
    console.warn('Amplitude API key not set, analytics disabled')
    return
  }
  amplitude.init(AMPLITUDE_API_KEY, {
    defaultTracking: {
      pageViews: true,
      sessions: true,
      formInteractions: false,
      fileDownloads: false,
    },
  })
}

export type AuthMethod = 'email' | 'google' | 'magic_link'

export interface UserProperties {
  user_id: string
  name: string | null
  native_language: string
  target_language: string
  cefr_level: CEFRLevel
  subscription_tier: 'free' | 'pro'
  is_pro: boolean
  created_at: string
}

export function identifyUser(userId: string, properties: Partial<UserProperties>) {
  if (!AMPLITUDE_API_KEY) return
  amplitude.setUserId(userId)
  const identifyObj = new amplitude.Identify()
  Object.entries(properties).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      identifyObj.set(key, value)
    }
  })
  amplitude.identify(identifyObj)
}

export function setUserProperties(properties: Partial<UserProperties>) {
  if (!AMPLITUDE_API_KEY) return
  const identifyObj = new amplitude.Identify()
  Object.entries(properties).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      identifyObj.set(key, value)
    }
  })
  amplitude.identify(identifyObj)
}

export function resetAnalytics() {
  if (!AMPLITUDE_API_KEY) return
  amplitude.reset()
}

function track(eventName: string, properties?: Record<string, unknown>) {
  if (!AMPLITUDE_API_KEY) return
  try {
    amplitude.track(eventName, properties)
  } catch (error) {
    // Capture analytics errors in Sentry but don't break the app
    captureException(error, { eventName, properties })
  }
}

// Tier 1: Core Conversion Events
export const analytics = {
  // Auth events
  userSignedUp: (method: AuthMethod) => {
    track('user_signed_up', { method })
  },

  userSignedIn: (method: AuthMethod) => {
    track('user_signed_in', { method })
  },

  // Onboarding events
  onboardingStepCompleted: (step: number, stepName: string) => {
    track('onboarding_step_completed', { step, step_name: stepName })
  },

  onboardingCompleted: (nativeLanguage: string, targetLanguage: string, level: CEFRLevel) => {
    track('onboarding_completed', {
      native_language: nativeLanguage,
      target_language: targetLanguage,
      level,
    })
  },

  // Session events
  sessionStarted: (sessionId: string) => {
    track('session_started', { session_id: sessionId })
  },

  sessionAgentJoined: (sessionId: string) => {
    track('session_agent_joined', { session_id: sessionId })
  },

  sessionMuted: (sessionId: string, durationSeconds: number) => {
    track('session_muted', { session_id: sessionId, duration_seconds: durationSeconds })
  },

  sessionUnmuted: (sessionId: string, durationSeconds: number) => {
    track('session_unmuted', { session_id: sessionId, duration_seconds: durationSeconds })
  },

  sessionEnded: (sessionId: string, durationSeconds: number, endedBy: 'user' | 'agent' | 'error') => {
    track('session_ended', {
      session_id: sessionId,
      duration_seconds: durationSeconds,
      ended_by: endedBy,
    })
  },

  // Session summary
  sessionSummaryViewed: (sessionId: string, score: number | null, status: string) => {
    track('session_summary_viewed', {
      session_id: sessionId,
      score,
      status,
    })
  },

  // Paywall
  paywallShown: (sessionsUsed: number, trigger: string) => {
    track('paywall_shown', { sessions_used: sessionsUsed, trigger })
  },

  // Settings
  settingsChanged: (fieldsChanged: string[]) => {
    track('settings_changed', { fields_changed: fieldsChanged })
  },
}
